/*
 * Lunvelis Partners · comportamiento de la capa Ophir 0.3.
 * Sin dependencias. Tres responsabilidades:
 *   1. aparición progresiva al entrar en pantalla (IntersectionObserver, una sola vez);
 *   2. pestañas accesibles para las modalidades de colaboración;
 *   3. cierre del menú "Más" con Escape, clic fuera o al elegir un enlace.
 * El contenido es legible sin este archivo. Con movimiento reducido no se oculta nada.
 */
(() => {
  if (window.__partnersMotionReady) return;
  window.__partnersMotionReady = true;

  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  clearTimeout(window.__partnersMotionGuard);

  /* ---- 1. aparición progresiva ---- */
  let observer;
  const reveal = (scope = document) => {
    const targets = [...scope.querySelectorAll('[data-reveal]:not(.is-in)')];
    if (!targets.length) return;
    for (const group of scope.querySelectorAll('[data-reveal-stagger]')) {
      [...group.querySelectorAll(':scope > [data-reveal]')].forEach((item, index) => item.style.setProperty('--i', String(index)));
    }
    if (reduced.matches || !('IntersectionObserver' in window)) {
      for (const target of targets) target.classList.add('is-in');
      return;
    }
    observer ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    for (const target of targets) observer.observe(target);
    /* Red de seguridad: si el navegador tarda en entregar los avisos del observador, lo que ya está a la vista aparece igualmente. */
    setTimeout(() => {
      for (const target of targets) {
        if (target.classList.contains('is-in')) continue;
        const box = target.getBoundingClientRect();
        if (box.height > 0 && box.top < innerHeight * 0.92 && box.bottom > 0) { target.classList.add('is-in'); observer.unobserve(target); }
      }
    }, 1200);
  };

  /* ---- 2. pestañas ---- */
  const enhanceTabs = (scope = document) => {
    for (const tabs of scope.querySelectorAll('[data-lp-tabs]:not([data-enhanced])')) {
      const panels = [...tabs.querySelectorAll('[data-tab-title]')];
      const list = tabs.querySelector('.lp-tabs__list');
      if (panels.length < 2 || !list) continue;
      const uid = Math.random().toString(36).slice(2, 7);
      const buttons = panels.map((panel, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lp-tab';
        button.setAttribute('role', 'tab');
        button.id = `lp-tab-${uid}-${index}`;
        if (panel.dataset.tabAccent) button.dataset.accent = panel.dataset.tabAccent;
        button.setAttribute('aria-controls', `lp-panel-${uid}-${index}`);
        const title = document.createElement('strong');
        title.textContent = panel.dataset.tabTitle;
        button.append(title);
        if (panel.dataset.tabHint) {
          const hint = document.createElement('span');
          hint.textContent = panel.dataset.tabHint;
          button.append(hint);
        }
        panel.id = `lp-panel-${uid}-${index}`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.tabIndex = 0;
        list.append(button);
        return button;
      });
      const select = (next, focus = false) => {
        buttons.forEach((button, index) => {
          const active = index === next;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
          panels[index].hidden = !active;
        });
        if (focus) buttons[next].focus();
      };
      list.addEventListener('click', (event) => {
        const button = event.target.closest('.lp-tab');
        if (button) select(buttons.indexOf(button));
      });
      list.addEventListener('keydown', (event) => {
        const current = buttons.indexOf(document.activeElement);
        if (current < 0) return;
        const last = buttons.length - 1;
        const map = { ArrowDown: current === last ? 0 : current + 1, ArrowRight: current === last ? 0 : current + 1, ArrowUp: current === 0 ? last : current - 1, ArrowLeft: current === 0 ? last : current - 1, Home: 0, End: last };
        if (!(event.key in map)) return;
        event.preventDefault();
        select(map[event.key], true);
      });
      list.setAttribute('role', 'tablist');
      tabs.dataset.enhanced = '';
      select(0);
    }
  };

  /* ---- 3. menú Más ---- */
  const closeMenus = (except) => {
    for (const details of document.querySelectorAll('.lp-nav details[open]')) if (details !== except) details.open = false;
  };
  document.addEventListener('click', (event) => {
    const inside = event.target.closest('.lp-nav details');
    if (!inside) closeMenus();
    else if (event.target.closest('.lp-nav__sheet a')) inside.open = false;
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.lp-nav details[open]');
    if (!open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  });
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.('.lp-nav details') && event.target.open) closeMenus(event.target);
  }, true);

  /* ---- 4. cabecera: estado compacto al bajar (sin listeners de scroll: un centinela observado) ---- */
  const watchScroll = () => {
    const main = document.getElementById('MainContent');
    if (!main || main.querySelector('.lp-sentinel') || !('IntersectionObserver' in window)) return;
    const sentinel = document.createElement('span');
    sentinel.className = 'lp-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    main.prepend(sentinel);
    new IntersectionObserver(([entry]) => root.classList.toggle('lp-scrolled', !entry.isIntersecting)).observe(sentinel);
  };

  /* ---- 5. brillo que sigue al cursor en superficies de cristal y botones; imán muy leve en CTAs ---- */
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  let frame = 0;
  let pending;
  const paintPointer = () => {
    frame = 0;
    const { event, glass, magnet } = pending;
    if (glass) {
      const box = glass.getBoundingClientRect();
      glass.style.setProperty('--mx', `${event.clientX - box.left}px`);
      glass.style.setProperty('--my', `${event.clientY - box.top}px`);
    }
    for (const el of document.querySelectorAll('[data-magnetic]')) {
      const box = el.getBoundingClientRect();
      const dx = event.clientX - (box.left + box.width / 2);
      const dy = event.clientY - (box.top + box.height / 2);
      const near = Math.hypot(dx, dy) < Math.max(box.width, 140) * 0.8;
      el.style.setProperty('--tx', near && magnet ? `${Math.max(-5, Math.min(5, dx * 0.08)).toFixed(1)}px` : '0px');
      el.style.setProperty('--ty', near && magnet ? `${Math.max(-4, Math.min(4, dy * 0.12)).toFixed(1)}px` : '0px');
    }
  };
  document.addEventListener('pointermove', (event) => {
    if (!fine.matches || event.pointerType === 'touch') return;
    pending = { event, glass: event.target.closest?.('[data-glass], .lp-btn'), magnet: !reduced.matches };
    if (!frame) frame = requestAnimationFrame(paintPointer);
  }, { passive: true });

  /* ---- 6. audio de bienvenida: solo se pide el archivo al pulsar play ---- */
  const fmt = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  const enhanceAudio = (scope = document) => {
    for (const box of scope.querySelectorAll('[data-lp-audio]:not([data-ready])')) {
      const button = box.querySelector('.lp-audio__button');
      const seek = box.querySelector('.lp-audio__seek');
      const current = box.querySelector('[data-lp-current]');
      const total = box.querySelector('[data-lp-total]');
      const status = box.querySelector('[data-lp-status]');
      const bars = [...box.querySelectorAll('[data-lp-wave] span')];
      if (!button || !seek || !box.dataset.src) continue;
      box.dataset.ready = '';
      let audio;
      const ensure = () => {
        if (audio) return audio;
        audio = new Audio();
        audio.preload = 'none';
        audio.src = box.dataset.src;
        audio.addEventListener('loadedmetadata', () => { total.textContent = fmt(audio.duration); seek.disabled = false; });
        audio.addEventListener('timeupdate', () => {
          const ratio = audio.duration ? audio.currentTime / audio.duration : 0;
          current.textContent = fmt(audio.currentTime);
          seek.value = String(Math.round(ratio * 100));
          seek.setAttribute('aria-valuetext', `${fmt(audio.currentTime)} de ${fmt(audio.duration || 0)}`);
          box.style.setProperty('--progress', `${(ratio * 100).toFixed(1)}%`);
          const lit = Math.round(ratio * bars.length);
          bars.forEach((bar, index) => bar.toggleAttribute('data-lit', index < lit));
        });
        audio.addEventListener('play', () => sync(true));
        audio.addEventListener('pause', () => sync(false));
        audio.addEventListener('ended', () => { audio.currentTime = 0; sync(false); });
        audio.addEventListener('error', () => { box.dataset.state = 'error'; status.textContent = 'No se pudo cargar el audio.'; });
        return audio;
      };
      const sync = (playing) => {
        box.dataset.state = playing ? 'playing' : 'paused';
        button.setAttribute('aria-pressed', String(playing));
        button.setAttribute('aria-label', playing ? box.dataset.labelPause : box.dataset.labelPlay);
        status.textContent = playing ? box.dataset.statusPlaying : box.dataset.statusPaused;
      };
      button.addEventListener('click', () => {
        const media = ensure();
        if (media.paused) {
          for (const other of document.querySelectorAll('audio')) other.pause();
          media.play().catch(() => sync(false));
        } else media.pause();
      });
      seek.addEventListener('input', () => {
        const media = ensure();
        if (media.duration) media.currentTime = (Number(seek.value) / 100) * media.duration;
      });
      document.addEventListener('visibilitychange', () => { if (document.hidden && audio && !audio.paused) audio.pause(); });
    }
  };

  /* ---- 7. mapa del ecosistema: nodo activo, detalle y línea resaltada ---- */
  const enhanceOrbit = (scope = document) => {
    for (const orbit of scope.querySelectorAll('[data-lp-orbit]:not([data-ready])')) {
      orbit.dataset.ready = '';
      const section = orbit.closest('section');
      const detail = section.querySelector('[data-orbit-detail]');
      const name = detail?.querySelector('[data-orbit-name]');
      const text = detail?.querySelector('[data-orbit-text]');
      const nodes = [...orbit.querySelectorAll('[data-orbit-node]')];
      const show = (node) => {
        const index = node?.dataset.orbitNode;
        orbit.dataset.active = index ?? '';
        for (const spoke of orbit.querySelectorAll('[data-spoke]')) spoke.toggleAttribute('data-on', spoke.dataset.spoke === index);
        for (const card of section.querySelectorAll('[data-orbit-card]')) card.toggleAttribute('data-on', card.dataset.orbitCard === index);
        if (!detail) return;
        name.textContent = node ? node.dataset.name : '';
        text.textContent = node ? node.dataset.text : detail.dataset.default;
      };
      for (const node of nodes) {
        node.addEventListener('pointerenter', () => show(node));
        node.addEventListener('focus', () => show(node));
        node.addEventListener('pointerleave', () => show(null));
        node.addEventListener('blur', () => show(null));
      }
      if (fine.matches && !reduced.matches) {
        orbit.addEventListener('pointermove', (event) => {
          const box = orbit.getBoundingClientRect();
          orbit.style.setProperty('--ox', `${(((event.clientX - box.left) / box.width) - 0.5).toFixed(3)}`);
          orbit.style.setProperty('--oy', `${(((event.clientY - box.top) / box.height) - 0.5).toFixed(3)}`);
        }, { passive: true });
        orbit.addEventListener('pointerleave', () => { orbit.style.setProperty('--ox', '0'); orbit.style.setProperty('--oy', '0'); });
      }
    }
  };

  /* ---- 8. titulares: cada palabra sube desde una máscara (solo con movimiento permitido) ---- */
  const splitText = (scope = document) => {
    for (const el of scope.querySelectorAll('[data-split]:not([data-split-ready])')) {
      el.dataset.splitReady = '';
      if (reduced.matches) continue;
      const text = el.textContent.trim().replace(/\s+/g, ' ');
      el.setAttribute('aria-label', text);
      el.textContent = '';
      text.split(' ').forEach((word, index) => {
        const mask = document.createElement('span');
        mask.className = 'lp-split';
        mask.setAttribute('aria-hidden', 'true');
        const inner = document.createElement('span');
        inner.textContent = word;
        inner.style.setProperty('--w', String(index));
        mask.append(inner);
        el.append(mask, ' ');
      });
      el.dataset.split = 'on';
    }
  };

  /* ---- 9. palabra cambiante: recorre la lista una vez y se detiene; se pausa con cursor o foco ---- */
  const enhanceRotators = (scope = document) => {
    for (const el of scope.querySelectorAll('[data-lp-rotator]:not([data-ready])')) {
      el.dataset.ready = '';
      const words = [...el.querySelectorAll('.lp-rot__word')];
      words[0]?.classList.add('is-on');
      if (words.length < 2 || reduced.matches) continue;
      const rot = el.querySelector('.lp-rot');
      const prefix = [...el.childNodes].filter((n) => n.nodeType === 3).map((n) => n.textContent).join('').replace(/\s+/g, ' ').trim();
      el.setAttribute('aria-label', `${prefix} ${words.map((w) => w.textContent.replace(/[.]$/, '')).join(', ')}.`);
      let index = 0;
      let paused = false;
      let timer;
      const widest = Math.max(...words.map((w) => w.getBoundingClientRect().width));
      rot.style.minInlineSize = `${Math.ceil(widest)}px`;
      words.forEach((w, i) => w.classList.toggle('is-on', i === 0));
      const next = () => {
        if (paused || document.hidden) { timer = setTimeout(next, 800); return; }
        words[index].classList.remove('is-on');
        index = (index + 1) % words.length;
        words[index].classList.add('is-on');
        if (index !== 0) timer = setTimeout(next, 2100);
      };
      const start = () => { timer = setTimeout(next, 2300); };
      const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { start(); io.disconnect(); } }, { threshold: 0.6 });
      io.observe(el);
      el.addEventListener('pointerenter', () => { paused = true; });
      el.addEventListener('pointerleave', () => { paused = false; });
      el.addEventListener('focusin', () => { paused = true; });
      el.addEventListener('focusout', () => { paused = false; });
    }
  };

  /* ---- 10. cifras estructurales (cuántas áreas, cuántas confirmadas): cuentan al entrar ---- */
  const enhanceCounts = (scope = document) => {
    for (const el of scope.querySelectorAll('[data-count]:not([data-ready])')) {
      el.dataset.ready = '';
      const target = Number(el.dataset.count);
      if (!Number.isFinite(target) || reduced.matches || !('IntersectionObserver' in window)) continue;
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const run = (now) => {
          const t = Math.min(1, (now - start) / 900);
          el.textContent = String(Math.round(target * (1 - Math.pow(1 - t, 3))));
          if (t < 1) requestAnimationFrame(run);
        };
        requestAnimationFrame(run);
      }, { threshold: 0.6 });
      el.textContent = '0';
      io.observe(el);
    }
  };

  /* ---- 11. inclinación leve en tarjetas (solo puntero fino, sin movimiento reducido) ---- */
  const enhanceTilt = (scope = document) => {
    for (const card of scope.querySelectorAll('[data-tilt]:not([data-tilt-ready])')) {
      card.dataset.tiltReady = '';
      card.addEventListener('pointermove', (event) => {
        if (!fine.matches || reduced.matches || event.pointerType === 'touch') return;
        const box = card.getBoundingClientRect();
        const x = (event.clientX - box.left) / box.width - 0.5;
        const y = (event.clientY - box.top) / box.height - 0.5;
        card.style.setProperty('--rx', `${(-y * 4).toFixed(2)}deg`);
        card.style.setProperty('--ry', `${(x * 5).toFixed(2)}deg`);
      }, { passive: true });
      card.addEventListener('pointerleave', () => { card.style.setProperty('--rx', '0deg'); card.style.setProperty('--ry', '0deg'); });
    }
  };

  /* ---- 12. haces de conexión (diagrama de conexión): curvas entre nodos medidas del DOM ---- */
  const enhanceBeams = (scope = document) => {
    for (const net of scope.querySelectorAll('[data-lp-beams]:not([data-ready])')) {
      net.dataset.ready = '';
      const stage = net.querySelector('.lp-net__stage');
      const svg = net.querySelector('.lp-net__svg');
      const hub = net.querySelector('[data-beam-node="hub"]');
      if (!stage || !svg || !hub) continue;
      const ns = 'http://www.w3.org/2000/svg';
      const draw = () => {
        const base = stage.getBoundingClientRect();
        svg.setAttribute('viewBox', `0 0 ${base.width} ${base.height}`);
        svg.replaceChildren();
        const point = (el, side) => {
          const b = el.getBoundingClientRect();
          const cx = b.left - base.left + b.width / 2;
          const cy = b.top - base.top + b.height / 2;
          return side === 'left' ? [b.left - base.left, cy] : side === 'right' ? [b.right - base.left, cy] : side === 'top' ? [cx, b.top - base.top] : [cx, b.bottom - base.top];
        };
        const link = (from, to, tone, delay) => {
          const a = from.getBoundingClientRect(), z = to.getBoundingClientRect();
          const horizontal = Math.abs((a.left + a.width / 2) - (z.left + z.width / 2)) > Math.abs((a.top + a.height / 2) - (z.top + z.height / 2));
          let p1, p2, d;
          if (horizontal) {
            const leftToRight = a.left < z.left;
            p1 = point(from, leftToRight ? 'right' : 'left'); p2 = point(to, leftToRight ? 'left' : 'right');
            const mid = (p1[0] + p2[0]) / 2;
            d = `M${p1[0]} ${p1[1]} C${mid} ${p1[1]} ${mid} ${p2[1]} ${p2[0]} ${p2[1]}`;
          } else {
            const topToBottom = a.top < z.top;
            p1 = point(from, topToBottom ? 'bottom' : 'top'); p2 = point(to, topToBottom ? 'top' : 'bottom');
            const mid = (p1[1] + p2[1]) / 2;
            d = `M${p1[0]} ${p1[1]} C${p1[0]} ${mid} ${p2[0]} ${mid} ${p2[0]} ${p2[1]}`;
          }
          const track = document.createElementNS(ns, 'path');
          track.setAttribute('d', d); track.setAttribute('class', 'lp-net__track'); track.setAttribute('pathLength', '100');
          const beam = document.createElementNS(ns, 'path');
          beam.setAttribute('d', d); beam.setAttribute('class', `lp-net__beam ${tone}`); beam.setAttribute('pathLength', '100');
          beam.style.setProperty('--d', `${delay}ms`);
          svg.append(track, beam);
        };
        net.querySelectorAll('[data-beam-node^="in-"]').forEach((node, i) => link(node, hub, 'lp-tone-in', i * 380));
        net.querySelectorAll('[data-beam-node^="out-"]').forEach((node, i) => link(hub, node, `lp-tone-${i}`, 700 + i * 380));
      };
      draw();
      new ResizeObserver(draw).observe(stage);
      document.fonts?.ready.then(draw);
    }
  };

  /* ---- 13. hoja de ruta: el paso en foco cambia la ilustración fija ---- */
  const enhanceRoadmap = (scope = document) => {
    for (const road of scope.querySelectorAll('[data-lp-roadmap]:not([data-ready])')) {
      road.dataset.ready = '';
      const steps = [...road.querySelectorAll('.lp-step')];
      if (!steps.length || !('IntersectionObserver' in window)) continue;
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) if (entry.isIntersecting) road.dataset.active = entry.target.dataset.step;
      }, { rootMargin: '-42% 0px -42% 0px', threshold: 0 });
      steps.forEach((step) => io.observe(step));
    }
  };

  /* ---- 14. comparador antes / después: arrastre, teclado (control de rango) y botones ---- */
  const enhanceCompare = (scope = document) => {
    for (const ui of scope.querySelectorAll('[data-lp-compare]:not([data-ready])')) {
      ui.dataset.ready = '';
      const range = ui.querySelector('.lp-compare__range');
      const section = ui.closest('section');
      const set = (value, animate = false) => {
        const pos = Math.max(0, Math.min(100, value));
        ui.classList.toggle('is-easing', animate && !reduced.matches);
        ui.style.setProperty('--pos', `${pos}%`);
        range.value = String(Math.round(pos));
        range.setAttribute('aria-valuetext', `${Math.round(pos)}% antes, ${100 - Math.round(pos)}% después`);
      };
      range.addEventListener('input', () => set(Number(range.value)));
      const drag = (event) => {
        const box = ui.getBoundingClientRect();
        set(((event.clientX - box.left) / box.width) * 100);
      };
      ui.addEventListener('pointerdown', (event) => {
        if (event.target === range) return;
        ui.setPointerCapture(event.pointerId);
        ui.classList.add('is-dragging');
        drag(event);
        const move = (e) => drag(e);
        const up = () => { ui.classList.remove('is-dragging'); ui.removeEventListener('pointermove', move); ui.removeEventListener('pointerup', up); ui.removeEventListener('pointercancel', up); };
        ui.addEventListener('pointermove', move); ui.addEventListener('pointerup', up); ui.addEventListener('pointercancel', up);
      });
      section.querySelectorAll('[data-compare-set]').forEach((button) => button.addEventListener('click', () => set(Number(button.dataset.compareSet), true)));
    }
  };

  /* ---- 15. cabecera sobre zona oscura: píldoras y logotipo cambian de variante ---- */
  const watchDark = () => {
    const zones = document.querySelectorAll('.lp-dark');
    if (!zones.length || !('IntersectionObserver' in window) || document.documentElement.dataset.darkWatch) return;
    document.documentElement.dataset.darkWatch = '';
    const active = new Set();
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) entry.isIntersecting ? active.add(entry.target) : active.delete(entry.target);
      root.classList.toggle('lp-on-dark', active.size > 0);
    }, { rootMargin: '0px 0px -92% 0px' });
    zones.forEach((zone) => io.observe(zone));
  };


  /* ---- 16. selector segmentado: rutas del proceso y tipos de membresía ---- */
  const enhanceSeg = (scope = document) => {
    for (const seg of scope.querySelectorAll('[data-lp-seg]:not(.is-ready)')) {
      const bar = seg.querySelector('[data-seg-bar]');
      const buttons = bar ? [...bar.querySelectorAll('[data-seg-value]')] : [];
      const panels = [...seg.querySelectorAll(':scope > [data-seg-panel]')];
      if (buttons.length < 2 || panels.length < 2) continue;
      bar.style.setProperty('--n', String(buttons.length));
      const byId = new Map(panels.filter((panel) => panel.id).map((panel) => [panel.id, panel.dataset.segPanel]));
      const select = (value, { hash = false, focus = false } = {}) => {
        const index = Math.max(0, buttons.findIndex((button) => button.dataset.segValue === value));
        buttons.forEach((button, i) => { button.setAttribute('aria-pressed', String(i === index)); button.tabIndex = i === index ? 0 : -1; });
        bar.style.setProperty('--i', String(index));
        panels.forEach((panel) => { panel.hidden = panel.dataset.segPanel !== buttons[index].dataset.segValue; });
        if (hash && seg.hasAttribute('data-seg-hash')) {
          const open = panels.find((panel) => !panel.hidden);
          if (open?.id) history.replaceState(null, '', location.pathname + location.search + '#' + open.id);
        }
        if (focus) buttons[index].focus();
        document.dispatchEvent(new CustomEvent('lp:seg', { detail: { seg, value: buttons[index].dataset.segValue } }));
      };
      bar.addEventListener('click', (event) => { const button = event.target.closest('[data-seg-value]'); if (button) select(button.dataset.segValue, { hash: true }); });
      bar.addEventListener('keydown', (event) => {
        const current = buttons.indexOf(document.activeElement);
        if (current < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const last = buttons.length - 1;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? last : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
        select(buttons[next].dataset.segValue, { hash: true, focus: true });
      });
      seg.classList.add('is-ready');
      const wanted = byId.get(location.hash.slice(1));
      select(wanted || buttons[0].dataset.segValue);
      if (wanted) requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' }));
      window.addEventListener('hashchange', () => {
        const value = byId.get(location.hash.slice(1));
        if (!value) return;
        select(value);
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start', behavior: reduced.matches ? 'auto' : 'smooth' });
      });
    }
  };

  /* ---- 17. proceso paso a paso: el paso activo y la línea de avance siguen al scroll (sin listeners de scroll) ---- */
  const enhanceProcess = (scope = document) => {
    for (const list of scope.querySelectorAll('[data-lp-steps]:not([data-enhanced])')) {
      list.dataset.enhanced = '';
      const steps = [...list.querySelectorAll(':scope > [data-step]')];
      const line = list.querySelector('.lp-steps__line');
      if (!steps.length || !line) continue;
      const setActive = (index) => {
        steps.forEach((step, i) => { step.classList.toggle('is-active', i === index); step.classList.toggle('is-done', i < index); });
        let ratio = 0;
        if (index >= 0) {
          const dot = steps[index].querySelector('.lp-pstep__dot');
          const y = steps[index].offsetTop + dot.offsetTop + dot.offsetHeight / 2;
          ratio = Math.max(0, Math.min(1, (y - line.offsetTop) / line.offsetHeight));
        }
        list.style.setProperty('--r', ratio.toFixed(3));
      };
      if (reduced.matches || !('IntersectionObserver' in window)) { steps.forEach((step) => step.classList.add('is-done')); list.style.setProperty('--r', '1'); continue; }
      const live = new Set();
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) entry.isIntersecting ? live.add(entry.target) : live.delete(entry.target);
        if (live.size) { setActive(Math.max(...[...live].map((step) => steps.indexOf(step)))); return; }
        const first = steps[0].getBoundingClientRect();
        const last = steps[steps.length - 1].getBoundingClientRect();
        if (first.top > innerHeight / 2) setActive(-1);
        else if (last.bottom < innerHeight / 2) setActive(steps.length - 1);
      }, { rootMargin: '-42% 0px -42% 0px' });
      steps.forEach((step) => io.observe(step));
    }
  };

  /* ---- 18. buscador de soluciones: una necesidad, un área ---- */
  const enhanceFinder = (scope = document) => {
    for (const finder of scope.querySelectorAll('[data-lp-finder]:not(.is-ready)')) {
      const list = finder.querySelector('.lp-finder__list');
      const needs = [...finder.querySelectorAll('.lp-need')];
      if (needs.length < 2 || !list) continue;
      const uid = Math.random().toString(36).slice(2, 7);
      const buttons = needs.map((need, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = 'lp-need-tab-' + uid + '-' + index;
        button.className = 'lp-finder__tab ' + ((need.className.match(/lp-tone-\d/) || [''])[0]);
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', 'lp-need-' + uid + '-' + index);
        const label = document.createElement('strong'); label.textContent = need.dataset.needLabel;
        const area = document.createElement('small'); area.textContent = need.dataset.needArea;
        button.append(label, area);
        need.id = 'lp-need-' + uid + '-' + index;
        need.setAttribute('role', 'tabpanel');
        need.setAttribute('aria-labelledby', button.id);
        list.append(button);
        return button;
      });
      const select = (next, focus = false) => {
        buttons.forEach((button, i) => { button.setAttribute('aria-selected', String(i === next)); button.tabIndex = i === next ? 0 : -1; needs[i].hidden = i !== next; });
        if (focus) buttons[next].focus();
      };
      list.addEventListener('click', (event) => { const button = event.target.closest('.lp-finder__tab'); if (button) select(buttons.indexOf(button)); });
      list.addEventListener('keydown', (event) => {
        const current = buttons.indexOf(document.activeElement);
        if (current < 0) return;
        const last = buttons.length - 1;
        const map = { ArrowDown: current === last ? 0 : current + 1, ArrowRight: current === last ? 0 : current + 1, ArrowUp: current === 0 ? last : current - 1, ArrowLeft: current === 0 ? last : current - 1, Home: 0, End: last };
        if (!(event.key in map)) return;
        event.preventDefault();
        select(map[event.key], true);
      });
      list.setAttribute('role', 'tablist');
      finder.classList.add('is-ready');
      select(0);
    }
  };

  /* ---- 19. membresías: precio mensual o anual con cifras que ruedan ---- */
  const enhanceBilling = (scope = document) => {
    for (const bar of scope.querySelectorAll('[data-billing]:not([data-enhanced])')) {
      bar.dataset.enhanced = '';
      const seg = bar.querySelector('[data-billing-bar]');
      const buttons = seg ? [...seg.querySelectorAll('[data-billing-value]')] : [];
      const host = bar.closest('.lp-plans__panel') || document;
      const prices = [...host.querySelectorAll('[data-price]')];
      if (buttons.length < 2 || !prices.length) continue;
      const format = new Intl.NumberFormat('es-MX');
      const roll = (node, from, to) => {
        if (reduced.matches || from === to) { node.textContent = format.format(to); return; }
        const t0 = performance.now();
        const step = (t) => {
          const k = Math.min(1, (t - t0) / 520);
          node.textContent = format.format(Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3))));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      prices.forEach((price) => { const sub = price.closest('.lp-plan')?.querySelector('[data-price-sub]'); if (sub) sub.dataset.month = sub.textContent; });
      const monthLabel = prices[0].querySelector('[data-price-per]').textContent;
      const apply = (mode) => {
        seg.style.setProperty('--n', '2');
        seg.style.setProperty('--i', mode === 'year' ? '1' : '0');
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.billingValue === mode)));
        prices.forEach((price) => {
          const num = price.querySelector('[data-price-num]');
          const per = price.querySelector('[data-price-per]');
          const sub = price.closest('.lp-plan')?.querySelector('[data-price-sub]');
          const month = Number(price.dataset.month);
          const year = Number(price.dataset.year);
          roll(num, Number(num.textContent.replace(/\D/g, '')) || month, mode === 'year' ? year : month);
          per.textContent = mode === 'year' ? '/año' : monthLabel;
          if (!sub) return;
          const saving = Math.max(0, month * 12 - year);
          sub.textContent = mode === 'year' ? 'Equivale a $' + format.format(Math.round(year / 12)) + ' al mes' + (saving ? ' · ahorras $' + format.format(saving) + ' al año' : '') : sub.dataset.month;
        });
      };
      seg.addEventListener('click', (event) => { const button = event.target.closest('[data-billing-value]'); if (button) apply(button.dataset.billingValue); });
      const wanted = new URLSearchParams(location.search).get('plan');
      const card = wanted ? host.querySelector('[data-plan="' + CSS.escape(wanted) + '"]') : null;
      if (card && location.hash !== '#world') {
        card.classList.add('is-focus');
        requestAnimationFrame(() => card.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' }));
      }
    }
  };

  /* ---- 20. «Antes de enviar»: lista de puntos con avance ---- */
  const enhanceBrief = (scope = document) => {
    for (const box of scope.querySelectorAll('[data-lp-brief]:not([data-enhanced])')) {
      box.dataset.enhanced = '';
      const items = [...box.querySelectorAll('[data-brief-item]')];
      const done = box.querySelector('[data-brief-done]');
      const meter = box.querySelector('.lp-brief__meter span');
      if (!items.length || !done || !meter) continue;
      const update = () => {
        const count = items.filter((item) => item.checked).length;
        done.textContent = String(count);
        meter.style.setProperty('--r', String(count / items.length));
        box.classList.toggle('is-ready', count === items.length);
      };
      items.forEach((item) => item.addEventListener('change', update));
      update();
    }
  };

  /* ---- 21. contacto: el tema elegido muestra el camino más directo ---- */
  const enhanceReasons = (scope = document) => {
    for (const wrap of scope.querySelectorAll('[data-lp-reasons]:not([data-enhanced])')) {
      wrap.dataset.enhanced = '';
      const radios = [...wrap.querySelectorAll('input[type="radio"][data-reason-title]')];
      const panel = wrap.querySelector('[data-reason-panel]');
      if (!radios.length || !panel) continue;
      const title = panel.querySelector('h3');
      const text = panel.querySelector('p:not(.lp-route__label)');
      const link = panel.querySelector('[data-reason-link]');
      const apply = (radio) => {
        title.textContent = radio.dataset.reasonTitle;
        text.textContent = radio.dataset.reasonText;
        link.firstChild.nodeValue = radio.dataset.reasonCta + ' ';
        link.href = radio.dataset.reasonHref;
        if (/^https?:/.test(radio.dataset.reasonHref)) link.rel = 'noopener'; else link.removeAttribute('rel');
        panel.classList.remove('is-swap');
        void panel.offsetWidth;
        panel.classList.add('is-swap');
      };
      radios.forEach((radio) => radio.addEventListener('change', () => apply(radio)));
      const wanted = new URLSearchParams(location.search).get('reason');
      const match = wanted ? radios.find((radio) => (radio.dataset.reasonKeys || '').split(',').map((key) => key.trim()).includes(wanted)) : null;
      if (match) { match.checked = true; apply(match); }
    }
  };

  const init = (scope) => { splitText(scope); reveal(scope); enhanceTabs(scope); enhanceAudio(scope); enhanceOrbit(scope); enhanceRotators(scope); enhanceCounts(scope); enhanceTilt(scope); enhanceBeams(scope); enhanceRoadmap(scope); enhanceCompare(scope); enhanceSeg(scope); enhanceProcess(scope); enhanceFinder(scope); enhanceBilling(scope); enhanceBrief(scope); enhanceReasons(scope); watchScroll(); watchDark(); };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  reduced.addEventListener('change', () => {
    root.classList.toggle('partners-motion', !reduced.matches);
    if (reduced.matches) for (const target of document.querySelectorAll('[data-reveal]')) target.classList.add('is-in');
  });
})();
